"use client"

import { useState } from "react"
import { motion } from "motion/react"
import { toast } from "@medusajs/ui"
import { useTranslations } from "next-intl"

import { ArrowRightIcon } from "@/components/ui/arrow-right"
import { BoxIcon } from "@/components/ui/box"
import { Button } from "@/components/ui/button"
import { DownloadIcon } from "@/components/ui/download"
import { WorkflowIcon } from "@/components/ui/workflow"

/**
 * 下载页区块（改造自 shadcn-space 的 `download-01` 模版）。
 *
 * 原模版是"一个平台一张卡"的九宫格（iOS / Android / Web / Mac / Linux / Windows /
 * 三个浏览器扩展），我们这里换成真正的入口：官网直连安装包、Gitee 镜像、
 * GitLab 镜像。下载区不再提供 GitHub 入口（2026-10-21 用户定），
 * Gitea 镜像也已撤下（2026-10-23 用户定：自建站开了全站登录墙），
 * kind 与图标一并删掉，不留隐藏卡 —— 页脚的 GitHub 社交图标也已移除（2026-10-21）。
 * 保留模版的结构（Badge + 标题 + 三列卡片，
 * 每张卡=图标框/标题/说明/描边胶囊按钮+箭头动效）与全部动效。
 *
 * 与模版的三处差异（都是必要改动，不是美观偏好）：
 * 1. 图标从 images.shadcnspace.com 的远程 SVG 换成本站已有的图标组件 —— 不依赖别人 CDN；
 * 2. `lucide-react` 没装，箭头用 `@/components/ui/arrow-right`；
 * 3. 卡片的跳转地址、文案全部由页面传入（多语言 + 地址随版本变）。
 */
export type DownloadCardKind = "installer" | "gitee" | "gitlab"

export type DownloadCard = {
  kind: DownloadCardKind
  title: string
  description: string
  /** 不可用（只有 unavailableLabel）时可以不传。 */
  buttonLabel?: string
  /**
   * 直接下载的地址。走门禁的卡（installer）不传 ——
   * 它的地址由 `/api/download/gate` 现给，见下面的 startGatedDownload。
   */
  href?: string
  unavailableLabel?: string
}

export type Download01Props = {
  heading: string
  subheading: string
  meta: string[]
  cards: DownloadCard[]
  checksumLabel: string
  shaShort: string
  shaFull: string
  copyLabel: string
  copiedLabel: string
  verifyLabel: string
  verifyHref: string
  stepsTitle: string
  steps: { title: string; body: string }[]
}

const ICONS: Record<DownloadCardKind, typeof DownloadIcon> = {
  installer: DownloadIcon,
  gitee: BoxIcon,
  gitlab: WorkflowIcon,
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: "easeOut" as const },
  },
}

export default function Download01({
  heading,
  subheading,
  meta,
  cards,
  checksumLabel,
  shaShort,
  shaFull,
  copyLabel,
  copiedLabel,
  verifyLabel,
  verifyHref,
  stepsTitle,
  steps,
}: Download01Props) {
  const t = useTranslations("Download")
  const [copied, setCopied] = useState(false)
  /** 额度用完（服务端 429）后，走门禁的卡置灰。 */
  const [quotaExhausted, setQuotaExhausted] = useState(false)
  const [pending, setPending] = useState<DownloadCardKind | null>(null)

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shaFull)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // 剪贴板被拒（非 https 或用户拒绝）时什么都不做：完整指纹本来就在页面上。
    }
  }

  /**
   * 走门禁的卡（官网直链）点下去先问服务端要地址。
   *
   * 为什么不在前端算额度：清一下缓存或者换个无痕窗口就绕过去了，只有服务端说的算。
   * 被挡住时把备用链接一起抛出来 —— 用户看到的是"换个地方下"，不是一句冷冰冰的失败。
   */
  const startGatedDownload = async (kind: "installer") => {
    setPending(kind)

    try {
      const response = await fetch(`/api/download/gate?k=${kind}`, {
        cache: "no-store",
      })
      const data = (await response.json().catch(() => null)) as {
        url?: string
        fallback?: { gitee?: string; gitlab?: string }
      } | null

      if (response.status === 200 && data?.url) {
        // 交给浏览器直接开始下（不要用客户端路由，它会把下载页自己顶掉）。
        window.location.assign(data.url)
        return
      }

      if (response.status === 429) {
        setQuotaExhausted(true)
        toast.error(t("downloadTooFrequent"), {
          description: t.rich("downloadTooFrequentDesc", {
            gitee: (chunks) => (
              <a className="underline" href={data?.fallback?.gitee}>
                {chunks}
              </a>
            ),
            gitlab: (chunks) => (
              <a className="underline" href={data?.fallback?.gitlab}>
                {chunks}
              </a>
            ),
          }),
          duration: 3000,
        })
        return
      }

      toast.error(t("downloadUnavailable"), { duration: 3000 })
    } catch {
      toast.error(t("downloadUnavailable"), { duration: 3000 })
    } finally {
      setPending(null)
    }
  }

  return (
    <section className="bg-background">
      <div className="py-16 sm:py-20 lg:py-20">
        <div className="mx-auto max-w-7xl px-4 lg:px-8 xl:px-16">
          {/* 页头 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="mb-12 flex flex-col items-center gap-4"
          >
            <h1 className="max-w-2xl text-center text-3xl leading-tight font-medium tracking-tight sm:text-4xl">
              {heading}
            </h1>
            <p className="max-w-xl text-center text-base font-normal text-muted-foreground">
              {subheading}
            </p>
            <div className="mt-1 flex flex-wrap items-center justify-center gap-2 text-sm text-muted-foreground">
              {meta.map((item) => (
                <span
                  key={item}
                  className="rounded-full border border-border px-3 py-1 whitespace-nowrap"
                >
                  {item}
                </span>
              ))}
            </div>
          </motion.div>

          {/* 下载卡片 */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3"
          >
            {cards.map((card) => {
              const Icon = ICONS[card.kind]
              // 走门禁的卡没有写死的地址，能不能点由额度决定；
              // 其余卡还是老规矩：没有 href 就是置灰。
              const gated = card.kind === "installer"
              const disabled = gated ? quotaExhausted : !card.href
              const busy = gated && pending === card.kind

              return (
                <motion.div
                  key={card.kind}
                  variants={itemVariants}
                  data-testid={`download-card-${card.kind}`}
                  className="flex w-full flex-col justify-between gap-8 overflow-hidden rounded-2xl border border-border bg-card p-7"
                >
                  <div className="rounded-lg border border-border p-3 w-fit">
                    <Icon size={24} className="size-6" />
                  </div>
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-1">
                      <p className="text-lg font-medium text-card-foreground">
                        {card.title}
                      </p>
                      <p className="text-sm font-normal text-muted-foreground">
                        {card.description}
                      </p>
                    </div>

                    {disabled ? (
                      <span
                        data-testid={
                          quotaExhausted
                            ? "download-quota-notice"
                            : `download-unavailable-${card.kind}`
                        }
                        className="inline-flex h-9 w-fit cursor-not-allowed items-center gap-1.5 rounded-full border border-border px-4 text-sm font-medium text-muted-foreground opacity-60"
                      >
                        {card.unavailableLabel}
                      </span>
                    ) : gated ? (
                      <Button
                        type="button"
                        variant="outline"
                        data-testid="download-direct-button"
                        disabled={busy}
                        onClick={() =>
                          startGatedDownload(card.kind as "installer")
                        }
                        className="group h-9 w-fit cursor-pointer gap-1.5 rounded-full bg-transparent px-4 text-sm font-medium shadow-xs"
                      >
                        {card.buttonLabel}
                        <ArrowRightIcon
                          size={16}
                          className="transition-all duration-200 ease-in-out group-hover:translate-x-1 group-hover:-rotate-45"
                        />
                      </Button>
                    ) : (
                      <Button
                        asChild
                        variant="outline"
                        className="group h-9 w-fit cursor-pointer gap-1.5 rounded-full bg-transparent px-4 text-sm font-medium shadow-xs"
                      >
                        {/* 原生 <a>：目标是 302 下载接口，走客户端路由反而会吞掉跳转。 */}
                        {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
                        <a
                          href={card.href}
                          data-testid={`download-mirror-${card.kind}`}
                        >
                          {card.buttonLabel}
                          <ArrowRightIcon
                            size={16}
                            className="transition-all duration-200 ease-in-out group-hover:translate-x-1 group-hover:-rotate-45"
                          />
                        </a>
                      </Button>
                    )}
                  </div>
                </motion.div>
              )
            })}
          </motion.div>

          {/* 指纹 */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="mt-10 flex flex-wrap items-center justify-center gap-3 text-sm text-muted-foreground"
          >
            <span className="font-medium text-foreground">{checksumLabel}</span>
            <code
              data-testid="download-sha-value"
              className="rounded-md border border-border bg-card px-2 py-1 font-mono text-xs"
            >
              {shaShort}
            </code>
            <Button
              variant="outline"
              size="sm"
              data-testid="download-sha-copy"
              onClick={copy}
              className="h-8 rounded-md px-2.5 text-xs"
            >
              {copied ? copiedLabel : copyLabel}
            </Button>
            <a
              href={verifyHref}
              data-testid="download-verify-link"
              className="underline underline-offset-4 hover:text-foreground"
              rel="noreferrer"
            >
              {verifyLabel}
            </a>
          </motion.div>

          {/* 安装三步 */}
          <div className="mt-14 border-t border-border pt-10">
            <p className="mb-5 text-center text-sm font-medium text-foreground">
              {stepsTitle}
            </p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              {steps.map((step, index) => (
                <div
                  key={step.title}
                  className="rounded-xl border border-border bg-card p-5"
                >
                  <p className="text-sm font-medium text-foreground">
                    {index + 1}. {step.title}
                  </p>
                  <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                    {step.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
